import { Button, FormControl } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import Page from "~/components/Page";
import TableControls from "~/components/TableControls";
import AdminUsersFilter, { TFilter, TFilterRole } from "~/components/AdminUsersFilter";
import { removeFromArray } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiGetInternalUsers } from "~/providers/api";
import { IInternalUser } from "~/providers/interfaces";
import RemoveInternalUserModal from "~/modals/RemoveInternalUserModal";
import CreateInternalUserModal from "~/modals/CreateInternalUserModal";
import InternalUsersTable from "~/tables/InternalUsersTable";
import { ADMIN_INTERNAL_USER } from "~/const";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	loading: boolean;
	isDirty: boolean;
	users: Array<IInternalUser>;
	usersOrig: Array<IInternalUser>;
	createUser: IInternalUser;
	editUser: IInternalUser;
	removeUser: IInternalUser;
	errorMsg: string;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	users: [],
	usersOrig: [],
	createUser: null,
	editUser: null,
	removeUser: null,
	errorMsg: "",
};

export default function AdminUsersPage() {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);

	function onRemove() {
		setState(prev => ({
			...prev,
			users: removeFromArray(prev.users, user => user.id === prev.removeUser.id),
			removeUser: null,
		}));
	}

	function onCreateUpdateUser(userUpdateData: IInternalUser) {
		setState(prev => {
			const users = prev.users;
			const user = users.filter(item => item.id === userUpdateData.id)[0];

			// edit
			if (user) {
				Object.assign(user, userUpdateData);
			} else {
				// new
				users.push(userUpdateData);
			}

			return {
				...prev,
				users,
				createUser: null,
				editUser: null,
				isDirty: false,
			};
		});
	}

	function onFilter(filterType: TFilter, filterRole: TFilterRole, filterValue: string) {
		setState(prev => {
			let users = prev.usersOrig.filter(item => {
				switch (filterType) {
					case "email":
						return item.email.indexOf(filterValue) !== -1;

					default:
				}

				return true;
			});

			if (filterRole !== "all") {
				users = users.filter(user => user.internalRole === filterRole);
			}

			return {
				...prev,
				users,
			};
		});
	}

	async function loadData() {
		updateState(INIT_STATE);

		const usersData = await apiGetInternalUsers(lang);

		updateState({
			loading: false,
			...usersData.noErrors
				? {
					users: usersData.data.slice(),
					usersOrig: usersData.data,
				}
				: {},
			errorMsg: usersData.errors.join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page title={trans("menu.adminInternalUsers")} breadcrumbs={[trans("administration"), trans("menu.users")]} checkInternalRole="internal_admin" onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.admin.users">
		<TableControls className="adminUsersPage__tableControls">
			<div className="adminUsersPage__filterCont">
				<AdminUsersFilter onFilter={onFilter} />
			</div>
			<FormControl size="small" className="adminUsersPage__addForm">
				<Button color="success" variant="contained" onClick={() => updateState({ createUser: ADMIN_INTERNAL_USER })} className="adminUsersPage__addBtn btn-with-icon">
					<AddIcon /> { trans("adminInternalUsersPage.addUser") }
				</Button>
			</FormControl>
		</TableControls>
		<InternalUsersTable loading={state.loading} items={state.users} onEdit={user => updateState({ editUser: user })} onRemove={user => updateState({ removeUser: user })} />
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.removeUser && <RemoveInternalUserModal user={state.removeUser} onClose={() => updateState({ removeUser: null })}
			onSave={onRemove} /> }
		{ state.createUser && <CreateInternalUserModal variant="create" userData={state.createUser} onClose={() => updateState({ createUser: null, isDirty: false })}
			onSave={onCreateUpdateUser} onChange={isDirty => updateState({ isDirty })} /> }
		{ state.editUser && <CreateInternalUserModal variant="update" userData={state.editUser} onClose={() => updateState({ editUser: null, isDirty: false })}
			onSave={onCreateUpdateUser} onChange={isDirty => updateState({ isDirty })} /> }
	</Page>;
}
